import { Link, useNavigate, useParams, useSearch } from "@tanstack/react-router";
import { LuExternalLink, LuMail } from "react-icons/lu";
import { useQuery } from "@/lib/convex";
import { useMemo, type ReactNode } from "react";
import { createColumnHelper } from "@tanstack/react-table";
import {
  Avatar,
  Badge,
  Box,
  Button,
  Card,
  Heading,
  HStack,
  Icon,
  SimpleGrid,
  Stack,
  Text,
  Wrap,
} from "@chakra-ui/react";
import type { FunctionReturnType } from "convex/server";
import { api } from "@convex/_generated/api";
import { LoadingCard } from "../components/LoadingCard";
import { PageHeader } from "../components/PageHeader";
import { DataTable } from "../components/DataTable";
import { FilterBar, type FilterNode } from "../components/FilterBar";
import { FilterCount } from "../components/FilterCount";
import { searchUpdate } from "@/lib/search";
import { RoleIcon, resolveMemberRole } from "../components/RoleIcon";
import { groupLink } from "@/lib/groupLink";
import { Tooltip } from "../components/ui/tooltip";

// Derived from the queries, not hand-written. A local shape plus an `as` cast
// silences the compiler, which is how listGroups changing shape reached the
// browser instead of the build.
type GroupsOverview = FunctionReturnType<typeof api.persons.listGroups>;

// A narrowing, not a set of independent switches: everything, then the open
// units, then one kind of open unit. The type options mean OPEN units of that
// type, because "closed core groups" is not a question anyone asks -- the four
// closed labs are historical and are looked at as one group or not at all.
export const GROUP_FILTERS = ["all", "open", "core", "adjunct", "facility", "admin", "closed"] as const;
type GroupFilter = (typeof GROUP_FILTERS)[number];
type GroupRow = GroupsOverview["groups"][number];
type SubUnitRow = GroupRow["subUnits"][number];
// Sub-units render as child rows, so the table works on either shape.
type GroupTreeRow = GroupRow | SubUnitRow;

// Narrowed on the parent, not the child: a parent row has every field a child
// has plus more, so excluding the child type would exclude the parent with it
// and leave `never`.
function isParentUnit(row: GroupTreeRow): row is GroupRow {
  return "subUnits" in row;
}
type GroupProfile = NonNullable<FunctionReturnType<typeof api.persons.getGroupProfileBySlug>>;
type GroupMember = GroupProfile["members"][number];

// Open or closed, not active or inactive. A unit closes; its people keep
// finishing theses and manuscripts, and its output history stays with it.
function GroupStatusBadge({ isClosed }: { isClosed: boolean }) {
  return (
    <Badge
      colorPalette={isClosed ? "gray" : "green"}
      variant={isClosed ? "subtle" : "solid"}
      textTransform="uppercase"
      letterSpacing="0.18em"
    >
      {isClosed ? "Closed" : "Open"}
    </Badge>
  );
}

function TypeBadge({ label }: { label: string }) {
  return (
    <Badge variant="outline" textTransform="uppercase" letterSpacing="0.18em">{label}</Badge>
  );
}

const groupColumnHelper = createColumnHelper<GroupTreeRow>();

function GroupsTable({ groups }: { groups: GroupRow[] }) {
  const data = useMemo<GroupTreeRow[]>(() => groups, [groups]);
  const columns = useMemo(
    () => [
      groupColumnHelper.accessor("name", {
        header: "Group",
        cell: ({ row }) => {
          const unit = row.original;
          const child = !isParentUnit(unit);
          const parentSlug = child ? (row.getParentRow()?.original.slug ?? null) : null;
          return (
            // Sub-units are indented, and link to their parent's page narrowed
            // to them: they have no page of their own.
            <Stack gap={1} pl={child ? 6 : 0}>
              {child ? (
                <Link to="/groups/$shortName" {...groupLink({ slug: unit.slug, parentSlug })}>
                  <Text fontWeight="medium" color="fg.muted" _hover={{ textDecoration: "underline" }}>{unit.name}</Text>
                </Link>
              ) : (
                <Link to="/groups/$shortName" params={{ shortName: unit.slug }}>
                  <Text fontWeight="semibold" _hover={{ textDecoration: "underline" }}>{unit.name}</Text>
                </Link>
              )}
              <HStack gap={2}>
                {/* Sub-units carry no code of their own. The three Molecular
                    Discovery Platform facilities inherited "MDP" from the
                    tracked group list, which is the platform's code, not
                    theirs -- showing it on each child just repeats the parent. */}
                {!child && unit.shortName ? (
                  <Text fontSize="xs" textTransform="uppercase" letterSpacing="0.18em" color="fg.muted">{unit.shortName}</Text>
                ) : null}
                {child ? (
                  <Badge size="sm" variant="subtle" colorPalette="gray">sub-group</Badge>
                ) : null}
              </HStack>
            </Stack>
          );
        },
      }),
      groupColumnHelper.accessor("typeLabel", {
        header: "Type",
        cell: ({ getValue }) => <TypeBadge label={getValue() ?? "Unknown"} />,
      }),
      groupColumnHelper.accessor("isClosed", {
        header: "Status",
        cell: ({ getValue }) => <GroupStatusBadge isClosed={getValue()} />,
      }),
      groupColumnHelper.accessor((row) => row.headNames[0] ?? "", {
        id: "heads",
        header: "Heads",
        cell: ({ row }) => <Text fontSize="sm" color="fg.muted">{row.original.headNames.length > 0 ? row.original.headNames.join(", ") : "—"}</Text>,
      }),
      // An accessor, not a display column: TanStack cannot sort a display column
      // because it has no value to compare. The accessor returns the member
      // count so the header sorts numerically, while the cell still renders the
      // breakdown beneath it.
      groupColumnHelper.accessor((row) => row.currentMemberCount, {
        id: "members",
        header: "Members",
        sortingFn: "basic",
        cell: ({ row }) => {
          const unit = row.original;
          const inSubUnits = isParentUnit(unit) ? unit.currentMemberCount - unit.ownMemberCount : 0;
          return (
            <Stack gap={0.5} fontSize="sm" color="fg.muted">
              {/* A parent's figure includes its sub-units, which is what it is
                  responsible for. Per-unit counts over-count people with several
                  affiliations by design; the deduplicated figure is on CeMMies. */}
              <Text>{unit.currentMemberCount} current</Text>
              {inSubUnits > 0 ? (
                <Text fontSize="xs">{inSubUnits} in sub-groups</Text>
              ) : null}
              {unit.cemmieCount === unit.currentMemberCount ? null : (
                <Text fontSize="xs">{unit.cemmieCount} count as CeMMies</Text>
              )}
            </Stack>
          );
        },
      }),
      groupColumnHelper.display({
        id: "website",
        header: "Website",
        cell: ({ row }) =>
          row.original.url ? (
            <Button asChild variant="ghost" size="sm" colorPalette="blue">
              <a href={row.original.url} target="_blank" rel="noreferrer">
                <LuExternalLink />
                Open
              </a>
            </Button>
          ) : (
            <Text fontSize="sm" color="fg.muted">—</Text>
          ),
      }),
    ],
    [],
  );

  return (
    <Box borderWidth="1px" borderColor="border">
      <DataTable
        data={data}
        columns={columns}
        getRowId={getGroupRowId}
        enableSorting
        stickyHeader
        getSubRows={(row) => (isParentUnit(row) ? row.subUnits : undefined)}
      />
    </Box>
  );
}

// Defined once, outside the component. An inline arrow is a new
// function on every render, and a table told its row identity has
// changed cannot skip anything.
const getGroupRowId = (row: { _id: string }) => row._id;
const getMemberRowId = (row: { affiliationId: string }) => row.affiliationId;

export function GroupsPage() {
  const overview = useQuery(api.persons.listGroups, {});
  // Open by default: the list answers "where does the institute work", and the
  // closed labs are history. Held in the address (?filter=closed), so the
  // default is the one value never written there.
  const filter: GroupFilter = useSearch({ from: "/_authed/groups" }).filter ?? "open";
  const navigate = useNavigate({ from: "/groups" });
  const setFilter = (next: GroupFilter) =>
    void navigate({ search: { filter: next === "open" ? undefined : next }, ...searchUpdate });

  const groups = overview?.groups ?? [];
  const matches = (group: (typeof groups)[number], key: GroupFilter) => {
    if (key === "all") return true;
    if (key === "closed") return group.isClosed;
    if (key === "open") return !group.isClosed;
    return !group.isClosed && group.type === key;
  };

  const visibleGroups = groups.filter((group) => matches(group, filter));
  const countFor = (key: GroupFilter) => groups.filter((group) => matches(group, key)).length;

  // Open and closed partition the units; the kinds of unit narrow what is
  // already open, since "closed core groups" is not a question anyone asks --
  // the four closed labs are looked at as one group or not at all. Everything
  // is the state before any of these, so it has no chip.
  const filterOptions: FilterNode<GroupFilter>[] = [
    {
      key: "open",
      label: "Open",
      count: countFor("open"),
      description: "Groups the institute currently works in",
      children: [
        { key: "core", label: "Core", count: countFor("core"), description: "CeMM's own research groups" },
        { key: "adjunct", label: "Adjunct", count: countFor("adjunct"), description: "Affiliated groups whose people are mostly employed elsewhere" },
        { key: "facility", label: "Facilities", count: countFor("facility"), description: "Technology and service facilities" },
        { key: "admin", label: "Administration", count: countFor("admin"), description: "Administration and its sub-groups" },
      ],
    },
    {
      key: "closed",
      label: "Closed",
      count: countFor("closed"),
      description: "No longer active. Some members may still be finishing their work.",
    },
  ];


  return (
    <Box minH="full" bg="bg.subtle">
      <PageHeader
        title="Groups"
        description="Institute research groups, facilities and administration, with their heads and membership."
        breadcrumbs={[{ label: "CeMMbase", to: "/" }, { label: "Groups" }]}
      />

      <Stack gap={8} px={{ base: 6, sm: 8, lg: 10 }} py={{ base: 6, lg: 8 }}>
        {overview === undefined ? (
          <LoadingCard label="Loading groups…" />
        ) : (
          <>
            <FilterBar
              label="Filter"
              options={filterOptions}
              value={filter}
              clearKey="all"
              onChange={setFilter}
            />

            {/* The count belongs with the table, not with the filters: it
                describes the rows, and 32px of section spacing between the two
                left it reading as a footnote to the chips. */}
            <Stack gap={2} align="stretch">
              <FilterCount
                shown={visibleGroups.length}
                total={countFor("all")}
                noun={["group", "groups"]}
                filtered={filter !== "all"}
                onClear={() => setFilter("all")}
              />
              <GroupsTable groups={visibleGroups} />
            </Stack>
          </>
        )}
      </Stack>
    </Box>
  );
}

/**
 * The person who runs a unit, or acts for them.
 *
 * Above the member table rather than pinned to the top of it: floating heads out
 * of alphabetical order makes them harder to find among their colleagues, and
 * "who runs this" is a different question from "who works here".
 */
const listFormat = new Intl.ListFormat("en", { style: "long", type: "conjunction" });

function LeaderCard({ member }: { member: GroupMember }) {
  const role = resolveMemberRole({ headRole: member.headRole, isStudent: member.isStudent });
  const roleText = member.isSubUnit && member.ledSubUnitNames.length > 0
    ? `${member.roleLabel} of ${listFormat.format(member.ledSubUnitNames)}`
    : member.roleLabel;
  return (
    <Card.Root borderWidth="1px" borderColor="border">
      <Card.Body gap={4} p={5}>
        <HStack gap={4} align="center">
          <Avatar.Root size="lg">
            <Avatar.Fallback name={member.fullName} />
            {member.profileImageUrl ? (
              <Avatar.Image src={member.profileImageUrl} alt={`${member.fullName} profile`} />
            ) : null}
          </Avatar.Root>
          <Stack gap={1} minW={0}>
            <HStack gap={2} minW={0}>
              <RoleIcon role={role} />
              {/* Which sub-unit they run, in the label rather than on a line
                  of its own -- and cut to one line, or a long sub-unit name
                  wraps and spends the line that moving it here saved. The
                  tooltip has it whole. */}
              <Tooltip content={roleText} disabled={roleText === member.roleLabel}>
                <Text truncate minW={0} fontSize="xs" fontWeight="semibold" textTransform="uppercase" letterSpacing="0.18em" color="fg.muted">
                  {roleText}
                </Text>
              </Tooltip>
            </HStack>
            {member.shortName ? (
              <Link to="/cemmies/$shortName" params={{ shortName: member.shortName }}>
                <Heading size="md" _hover={{ textDecoration: "underline" }}>{member.fullName}</Heading>
              </Link>
            ) : (
              <Heading size="md">{member.fullName}</Heading>
            )}
            <Text fontSize="sm" color="fg.muted">{member.positionLabel}</Text>
          </Stack>
        </HStack>
      </Card.Body>
    </Card.Root>
  );
}

const memberColumnHelper = createColumnHelper<GroupMember>();

function MemberTable({ members, emptyLabel }: { members: GroupMember[]; emptyLabel: string }) {
  const data = useMemo(() => members, [members]);
  const columns = useMemo(
    () => [
      memberColumnHelper.accessor("fullName", {
        header: "Name",
        cell: ({ row }) => {
          const member = row.original;
          return (
            <HStack gap={3}>
              <Avatar.Root size="md">
                <Avatar.Fallback name={member.fullName} />
                {member.profileImageUrl ? (
                  <Avatar.Image src={member.profileImageUrl} alt={`${member.fullName} profile`} />
                ) : null}
              </Avatar.Root>
              <Box>
                {member.shortName ? (
                  <Link to="/cemmies/$shortName" params={{ shortName: member.shortName }}>
                    <Text fontWeight="semibold" _hover={{ textDecoration: "underline" }}>{member.fullName}</Text>
                  </Link>
                ) : (
                  <Text fontWeight="semibold">{member.fullName}</Text>
                )}
                <Text fontSize="xs" color="fg.muted">{member.shortName ?? "No user short name"}</Text>
              </Box>
            </HStack>
          );
        },
      }),
      memberColumnHelper.accessor("email", {
        header: "Email",
        cell: ({ row }) => (
          <HStack gap={2} fontSize="sm" color="fg.muted">
            <Icon as={LuMail} boxSize={4} />
            <Text>{row.original.email}</Text>
          </HStack>
        ),
      }),
      memberColumnHelper.display({
        id: "position",
        header: "Position",
        cell: ({ row }) => {
          const member = row.original;
          // Role and position in one column: the role is an icon in front of the
          // position, using the same symbols as the CeMMies list so a person
          // does not change shape depending on which page you arrived from.
          const role = resolveMemberRole({
            headRole: member.headRole,
            isStudent: member.isStudent,
          });
          return (
            <Stack gap={1} fontSize="sm" color="fg.muted">
              <HStack gap={2}>
                <RoleIcon role={role} />
                <Text>{member.positionLabel || "—"}</Text>
                {member.isPrimary ? null : (
                  // Their primary unit is elsewhere, so this is not where their
                  // activities are supervised.
                  <Badge size="sm" variant="subtle" colorPalette="gray">secondary</Badge>
                )}
              </HStack>
              {/* A non-counting basis: on site, outside the headcount. Shown
                  only where it applies, so it reads as an exception. */}
              {member.countsAsCeMMie ? null : (
                <Text fontSize="xs" color="orange.fg">{member.basisLabel}</Text>
              )}
              {/* Which sub-unit they are in, when viewing a parent. More than
                  one where a person runs several -- the head of Proteomics and
                  Metabolomics is one member of the platform, listed once. */}
              {member.isSubUnit || member.unitNames.length > 1 ? (
                <Text fontSize="xs">{member.unitNames.join(" · ")}</Text>
              ) : null}
            </Stack>
          );
        },
      }),
      memberColumnHelper.display({
        id: "status",
        header: "Status",
        cell: ({ row }) => (
          <Badge
            colorPalette={row.original.status === "cemmie" ? "green" : "gray"}
            variant={row.original.status === "cemmie" ? "solid" : "subtle"}
            textTransform="uppercase"
            letterSpacing="0.18em"
          >
            {row.original.statusLabel}
          </Badge>
        ),
      }),
    ],
    [],
  );

  if (members.length === 0) {
    return (
      <Box borderWidth="1px" borderStyle="dashed" borderColor="border" px={5} py={6}>
        <Text fontSize="sm" color="fg.muted">{emptyLabel}</Text>
      </Box>
    );
  }

  return (
    <Box borderWidth="1px" borderColor="border">
      <DataTable
        data={data}
        columns={columns}
        getRowId={getMemberRowId}
        enableSorting
        stickyHeader
      />
    </Box>
  );
}

/**
 * One sub-group filter: a link, because the filter is part of the address.
 *
 * Replacing rather than pushing, so that Back leaves the page instead of
 * stepping back through every filter that was tried; and without the scroll
 * reset every navigation otherwise does, so the list does not jump to the top
 * of the page under the reader.
 */
function SubUnitFilterLink({
  groupSlug,
  subUnit,
  active,
  count,
  children,
}: {
  groupSlug: string;
  subUnit: string | undefined;
  active: boolean;
  count: number;
  children: ReactNode;
}) {
  const content = (
    <>
      {children}
      <Badge ml={2} size="sm" variant={active ? "solid" : "subtle"}>{count}</Badge>
    </>
  );
  // An empty sub-group stays visible, so the structure reads whole, but is not
  // a link: there is nothing to narrow to.
  if (count === 0 && !active) {
    return <Button size="sm" variant="outline" colorPalette="gray" disabled>{content}</Button>;
  }
  return (
    <Button asChild size="sm" variant={active ? "solid" : "outline"} colorPalette={active ? "blue" : "gray"}>
      <Link to="/groups/$shortName" params={{ shortName: groupSlug }} search={subUnit === undefined ? {} : { unit: subUnit }} replace resetScroll={false}>
        {content}
      </Link>
    </Button>
  );
}

export function GroupProfilePage() {
  const params = useParams({ from: "/_authed/groups/$shortName", structuralSharing: true });
  const search = useSearch({ from: "/_authed/groups/$shortName" });
  // Resolved by slug: short names are display codes and are not unique -- all
  // three Molecular Discovery Platform facilities carry "MDP".
  const profile = useQuery(api.persons.getGroupProfileBySlug, {
    slug: params.shortName,
  });
  const subUnitSlug = search.unit ?? null;
  const navigate = useNavigate({ from: "/groups/$shortName" });

  // Which sub-unit the member list is narrowed to, or null for everyone. The
  // address holds it, so a filtered list can be linked to and survives a
  // reload. The parent's own slug narrows to its direct members.
  const subUnitFilter = subUnitSlug === null || !profile
    ? null
    : subUnitSlug === profile.group.slug
      ? profile.group._id
      : (profile.group.children.find((child) => child.slug === subUnitSlug)?._id ?? null);

  const shownMembers = subUnitFilter === null
    ? (profile?.currentMembers ?? [])
    : (profile?.currentMembers ?? []).filter((member) => member.unitId === subUnitFilter);

  // The unit's own leadership first, then its sub-units'. On the Molecular
  // Discovery Platform that puts the head of the platform ahead of the three
  // facility heads, who would otherwise sort alphabetically among them and bury
  // the person the page is actually about.
  //
  // Heads before deputies within each level, then alphabetical. A unit may have
  // several of either.
  const leaders = (profile?.currentMembers ?? [])
    .filter((member) => member.headRole !== null)
    .sort(
      (left, right) =>
        Number(left.isSubUnit) - Number(right.isSubUnit) ||
        Number(right.headRole === "head") - Number(left.headRole === "head") ||
        left.fullName.localeCompare(right.fullName),
    );

  return (
    <Box minH="full" bg="bg.subtle">
      <PageHeader
        title={profile?.group.name ?? params.shortName}
        description={profile?.group.description ?? ""}
        loading={profile === undefined}
        breadcrumbs={[{ label: "CeMMbase", to: "/" }, { label: "Groups", to: "/groups" }, { label: profile?.group.name ?? params.shortName }]}
        actions={profile ? (
          <HStack flexWrap="wrap" justify="flex-end" gap={2}>
            <GroupStatusBadge isClosed={profile.group.isClosed} />
            <TypeBadge label={profile.group.typeLabel ?? "Unknown"} />
            <Badge variant="outline" textTransform="uppercase" letterSpacing="0.18em">{profile.group.shortName}</Badge>
            {profile.group.url ? (
              <Button asChild variant="outline">
                <a href={profile.group.url} target="_blank" rel="noreferrer">
                  <LuExternalLink />
                  Group website
                </a>
              </Button>
            ) : null}
          </HStack>
        ) : null}
      />

      <Stack gap={8} px={{ base: 6, sm: 8, lg: 10 }} py={{ base: 6, lg: 8 }}>
        {profile === undefined ? (
          <LoadingCard label="Loading group profile…" />
        ) : profile === null ? (
          <Card.Root borderWidth="1px" borderStyle="dashed" borderColor="border">
            <Card.Body gap={3} p={8} textAlign="center">
              <Heading size="xl">No group found</Heading>
              <Text fontSize="sm" color="fg.muted">No group with short name <Text as="span" fontWeight="semibold" color="fg">{params.shortName}</Text> exists in CeMMbase.</Text>
              <Box>
                <Button asChild colorPalette="blue">
                  <Link to="/groups">Back to groups</Link>
                </Button>
              </Box>
            </Card.Body>
          </Card.Root>
        ) : (
          <>
            {/* Who runs the unit, rather than a row of figures. The member
                count moved into the table heading, the head count is these
                cards, and type and status are already in the header tags -- a
                card each for those repeated what the page had said. */}
            {leaders.length > 0 ? (
              <SimpleGrid as="section" gap={4} columns={{ base: 1, md: 2, xl: 3, "2xl": 4 }}>
                {leaders.map((leader) => (
                  <LeaderCard key={leader.affiliationId} member={leader} />
                ))}
              </SimpleGrid>
            ) : null}

            <Stack as="section" gap={4}>
              <Box>
                <Heading size="xl">
                  {profile.stats.currentMembers} current member
                  {profile.stats.currentMembers === 1 ? "" : "s"}
                  {profile.stats.cemmies === profile.stats.currentMembers
                    ? ""
                    : ` · ${profile.stats.cemmies} count as CeMMies`}
                </Heading>
                {profile.group.children.length > 0 ? (
                  <Text mt={2} fontSize="sm" color="fg.muted">Including the members of its sub-groups.</Text>
                ) : null}
              </Box>

              {/* Sub-units filter this list rather than having pages of their
                  own, which would split Administration's people across eleven
                  views with no head and no reporting on any of them. */}
              {profile.group.children.length > 0 ? (
                <Wrap gap={2}>
                  <SubUnitFilterLink groupSlug={profile.group.slug} subUnit={undefined} active={subUnitFilter === null} count={profile.stats.currentMembers}>
                    All
                  </SubUnitFilterLink>
                  <SubUnitFilterLink groupSlug={profile.group.slug} subUnit={profile.group.slug} active={subUnitFilter === profile.group._id} count={profile.stats.ownMembers}>
                    {profile.group.name}
                  </SubUnitFilterLink>
                  {profile.group.children.map((child) => (
                    <SubUnitFilterLink key={child._id} groupSlug={profile.group.slug} subUnit={child.slug} active={subUnitFilter === child._id} count={child.memberCount}>
                      {child.name}
                    </SubUnitFilterLink>
                  ))}
                </Wrap>
              ) : null}

              {profile.group.children.length > 0 ? (
                <FilterCount
                  shown={shownMembers.length}
                  total={profile.currentMembers.length}
                  noun={["member", "members"]}
                  filtered={subUnitFilter !== null}
                  onClear={() => void navigate({ search: {}, ...searchUpdate })}
                />
              ) : null}
              <MemberTable
                members={shownMembers}
                emptyLabel="No current members are assigned to this group."
              />
            </Stack>

            <Stack as="section" gap={4}>
              <Box>
                <Heading size="xl">
                  {profile.stats.pastMembers} past member
                  {profile.stats.pastMembers === 1 ? "" : "s"}
                </Heading>
                <Text mt={2} fontSize="sm" color="fg.muted">People who have left the group.</Text>
              </Box>
              <MemberTable members={profile.pastMembers} emptyLabel="No past members are assigned to this group." />
            </Stack>
          </>
        )}
      </Stack>
    </Box>
  );
}
